/**
 * ItemCreationScreen — Product Master Registration (Phase 1: Layout + Tabs + Header)
 * Uses: erp-theme.css classes + shared ERPComponents
 * Pattern: Dense ERP layout matching the uploaded reference
 * API-ready: All lookups + form submission wired to real endpoints
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Save, X, RotateCcw, FileText, Tag, Package, DollarSign, Warehouse,
  Calculator, Hash, Barcode, Building2, Box, Calendar, Plus, Search,
  Settings, RefreshCw, Grid3X3, Upload, Palette, Users, ToggleLeft,
  ChevronLeft, Info, Percent, Clock, AlertCircle, Truck, CreditCard, Trash2
} from 'lucide-react';
import '../../../styles/erp-theme.css';
import DynamicFieldRenderer from '../../../components/erp/DynamicFieldRenderer';
import ERPEditableGrid from '../../../components/erp/ERPEditableGrid';
import { API_BASE as API, getToken} from '../../../lib/api';

const tkn = () => getToken() || '';
const hdrs = () => ({ Authorization: `Bearer ${tkn()}`, 'Content-Type': 'application/json' });

/* ═══════════════════════════════════════════
   INITIAL STATE
═══════════════════════════════════════════ */
const EMPTY_FORM = {
  // Header
  transNo: '', skuPlu: '', eanCode: '', manufacturerId: '', manufacturerName: '',
  // Metrics
  minQty: '0', rol: '0', maxQty: '0', leadTime: '30', expPeriod: '365',
  // Procurement
  agentId: '', agentName: '', supplierId: '', supplierName: '',
  paymentTerms: '', creditPeriod: '0', interest: '0', purchOfficerId: '', purchOfficerName: '',
  // Pricing
  priceQty: '1', mrp: '0.00', freeQty: '0', discPer: '0', rate: '0.00',
  purchDisc: '0', ownMrp: '0.00', wsp: '0.00', listMrp: '0.00', reverseMrpPct: '0',
  wspMarkdown: '0.00', mrpMarkdown: '0.00',
  // Classification
  divisionId: '', divisionName: '', deptId: '', deptName: '',
  categoryId: '', categoryName: '', brandId: '', brandName: '',
  // Tax
  inputTaxId: '', inputTaxName: '', outputTaxId: '', outputTaxName: '',
  cessValue: '0', baseUnitId: '', baseUnitName: '',
  // Article Details
  articleCode: '', articleName: '', itemName: '', barcode: '', oemBarcode: '', hsnCode: '',
  // Control
  invType: 'inventory', itemStatus: 'active',
  stkValMethod: 'FIFO', markupMethod: 'percentage', markdownMethod: 'percentage',
  markupLimit: '0', markdownLimit: '0',
  // Branding
  displayItemRevenue: '', brandingImage: '',
  promoterSupport: 'no', revenue1: '', revenue2: '', revenue3: '',
};

/* ═══════════════════════════════════════════
   COMPACT FIELD COMPONENTS (erp-theme.css)
═══════════════════════════════════════════ */
const F = ({ label, value, onChange, required, disabled, type = 'text', placeholder, suffix, testId, style, className = '' }) => (
  <div className={`erp-form-row ${className}`} style={style}>
    <label className={`erp-form-label${required ? ' required' : ''}`}>{label}</label>
    <div className="erp-form-control">
      <input type={type} value={value || ''} onChange={e => onChange?.(e.target.value)} disabled={disabled}
        className="erp-input" placeholder={placeholder} data-testid={testId}
        onKeyDown={e => { if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') { e.preventDefault(); const els = Array.from(e.target.closest('form')?.querySelectorAll('input:not([disabled]),select:not([disabled])') || []); const idx = els.indexOf(e.target); if (idx >= 0 && idx < els.length - 1) els[idx + 1].focus(); } }} />
      {suffix && <span style={{ fontSize: 10, color: 'var(--erp-text-muted)', marginLeft: 4 }}>{suffix}</span>}
    </div>
  </div>
);

const FL = ({ label, value, onChange, onLookup, required, disabled, testId, placeholder }) => (
  <div className="erp-form-row">
    <label className={`erp-form-label${required ? ' required' : ''}`}>{label}</label>
    <div className="erp-form-control" style={{ display: 'flex', gap: 4 }}>
      <input value={value || ''} onChange={e => onChange?.(e.target.value)} readOnly={!!onLookup} className="erp-input" style={{ flex: 1 }} data-testid={testId} placeholder={placeholder} disabled={disabled} />
      {onLookup && <button className="erp-btn-icon" style={{ border: '1px solid var(--erp-border)' }} onClick={onLookup} disabled={disabled}><Search size={12} /></button>}
    </div>
  </div>
);

const FS = ({ label, value, onChange, options, required, disabled, testId }) => (
  <div className="erp-form-row">
    <label className={`erp-form-label${required ? ' required' : ''}`}>{label}</label>
    <div className="erp-form-control">
      <select value={value || ''} onChange={e => onChange?.(e.target.value)} className="erp-input erp-select" disabled={disabled} data-testid={testId}>
        <option value="">--Select--</option>
        {(options || []).map(o => <option key={o.value || o} value={o.value || o}>{o.label || o}</option>)}
      </select>
    </div>
  </div>
);

const MetricCard = ({ label, value, icon: Icon, color = '#4A90D9' }) => (
  <div style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid var(--erp-border)', background: 'var(--erp-surface)', minWidth: 80 }}>
    <div style={{ fontSize: 9, color: 'var(--erp-text-muted)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
      {Icon && <Icon size={10} />}{label}
    </div>
    <div style={{ fontSize: 14, fontWeight: 700, fontFamily: 'var(--erp-font-mono)', color, marginTop: 2 }}>{value}</div>
  </div>
);

/* ═══════════════════════════════════════════
   TAB DEFINITIONS
═══════════════════════════════════════════ */
const TABS = [
  { id: 'sku', label: 'SKU/PLU Info', icon: Tag },
  { id: 'units', label: 'Multiple Units', icon: Package },
  { id: 'rates', label: 'Multiple Rates', icon: DollarSign },
  { id: 'suppliers', label: 'Other Suppliers', icon: Truck },
  { id: 'control', label: 'Control Option', icon: Settings },
  { id: 'branding', label: 'Branding', icon: Palette },
  { id: 'import', label: 'Import', icon: Upload },
];

/* ═══════════════════════════════════════════
   LOOKUP MODAL
═══════════════════════════════════════════ */
const LookupModal = ({ title, apiUrl, columns, onSelect, onClose }) => {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}${apiUrl}`, { headers: { Authorization: `Bearer ${tkn()}` } });
        const j = await r.json();
        setData(j.success ? (j.data || j.result || []) : (Array.isArray(j) ? j : []));
      } catch { setData([]); }
      setLoading(false);
    })();
  }, [apiUrl]);

  const filtered = search ? data.filter(row => columns.some(c => String(row[c.field] || '').toLowerCase().includes(search.toLowerCase()))) : data;

  return (
    <div className="erp-modal-overlay" data-testid="lookup-modal">
      <div className="erp-modal" style={{ width: 600 }}>
        <div className="erp-modal-header">
          <span className="erp-modal-title">{title}</span>
          <button className="erp-modal-close" onClick={onClose}>X</button>
        </div>
        <div className="erp-modal-search">
          <Search size={12} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." className="erp-input" style={{ flex: 1, fontSize: 11 }} autoFocus />
        </div>
        <div className="erp-modal-body" style={{ maxHeight: 300, overflow: 'auto' }}>
          {loading ? <div style={{ padding: 20, textAlign: 'center', fontSize: 11 }}>Loading...</div> : (
            <table className="erp-grid">
              <thead><tr>{columns.map(c => <th key={c.field}>{c.header}</th>)}</tr></thead>
              <tbody>
                {filtered.length === 0 ? <tr><td colSpan={columns.length} style={{ textAlign: 'center', padding: 16 }}>No data</td></tr> : (
                  filtered.slice(0, 50).map((row, i) => (
                    <tr key={i} onClick={() => onSelect(row)} style={{ cursor: 'pointer' }}>
                      {columns.map(c => <td key={c.field}>{row[c.field] || ''}</td>)}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   MAIN SCREEN
═══════════════════════════════════════════ */
const ItemCreationScreen = ({ theme = 'light', onClose }) => {
  const isDark = theme === 'dark';
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [activeTab, setActiveTab] = useState('sku');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [lookup, setLookup] = useState(null);
  const [itemCount, setItemCount] = useState(0);
  // Dynamic field values per section
  const [dynClassification, setDynClassification] = useState({});
  const [dynArticle, setDynArticle] = useState({});
  const [dynPricing, setDynPricing] = useState({});
  const [dynControl, setDynControl] = useState({});
  // Editable grid rows for tabs
  const [unitRows, setUnitRows] = useState([]);
  const [rateRows, setRateRows] = useState([]);
  const [supplierRows, setSupplierRows] = useState([]);
  const token = tkn();
  const userRole = (() => { try { return JSON.parse(localStorage.getItem('retailcliq_user') || '{}').role || ''; } catch { return ''; } })();

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Load item count on mount
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/inventory/items?page=1&pageSize=1`, { headers: { Authorization: `Bearer ${tkn()}` } });
        const j = await r.json();
        if (j.total) setItemCount(j.total);
      } catch {}
    })();
  }, []);

  // Auto-calc markdown when pricing changes
  useEffect(() => {
    const mrp = parseFloat(form.mrp) || 0;
    const rate = parseFloat(form.rate) || 0;
    const wsp = parseFloat(form.wsp) || 0;
    if (mrp > 0) {
      set('wspMarkdown', mrp > 0 ? ((mrp - wsp) / mrp * 100).toFixed(2) : '0.00');
      set('mrpMarkdown', mrp > 0 ? ((mrp - rate) / mrp * 100).toFixed(2) : '0.00');
    }
  }, [form.mrp, form.rate, form.wsp]);

  const handleSave = async () => {
    if (!form.itemName?.trim()) { setError('Item Name is required'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...form, dynamicFields: { ...dynClassification, ...dynArticle, ...dynPricing, ...dynControl }, units: unitRows, rates: rateRows, suppliers: supplierRows };
      const r = await fetch(`${API}/api/inventory/items`, { method: 'POST', headers: hdrs(), body: JSON.stringify(payload) });
      const j = await r.json();
      if (j.success) {
        // Save dynamic field values
        const recId = j.data?.itemId || form.transNo || form.skuPlu;
        if (recId && Object.keys(dynClassification).length > 0)
          await fetch(`${API}/api/dynamic-fields/values`, { method: 'POST', headers: hdrs(), body: JSON.stringify({ screen: 'ItemMaster_Classification', recordId: recId, values: dynClassification }) });
        if (recId && Object.keys(dynArticle).length > 0)
          await fetch(`${API}/api/dynamic-fields/values`, { method: 'POST', headers: hdrs(), body: JSON.stringify({ screen: 'ItemMaster_Article', recordId: recId, values: dynArticle }) });
        if (recId && Object.keys(dynPricing).length > 0)
          await fetch(`${API}/api/dynamic-fields/values`, { method: 'POST', headers: hdrs(), body: JSON.stringify({ screen: 'ItemMaster_Pricing', recordId: recId, values: dynPricing }) });
        if (recId && Object.keys(dynControl).length > 0)
          await fetch(`${API}/api/dynamic-fields/values`, { method: 'POST', headers: hdrs(), body: JSON.stringify({ screen: 'ItemMaster_Control', recordId: recId, values: dynControl }) });
        setForm({ ...EMPTY_FORM }); setDynClassification({}); setDynArticle({}); setDynPricing({}); setDynControl({});
        setItemCount(c => c + 1);
      }
      else setError(j.error || 'Save failed');
    } catch { setError('Save failed'); }
    setSaving(false);
  };

  const handleClear = () => { setForm({ ...EMPTY_FORM }); setDynClassification({}); setDynArticle({}); setDynPricing({}); setDynControl({}); setUnitRows([]); setRateRows([]); setSupplierRows([]); setError(''); };

  // Lookup helpers
  const openLookup = (title, apiUrl, columns, onSelect) => setLookup({ title, apiUrl, columns, onSelect });

  /* ═══════════════════════════════════════════
     TAB: SKU/PLU INFO
  ═══════════════════════════════════════════ */
  const renderSKUTab = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Procurement */}
      <div className="erp-form-card">
        <div style={{ padding: '6px 12px', borderBottom: '1px solid var(--erp-border)', fontSize: 11, fontWeight: 700, color: 'var(--erp-primary)', display: 'flex', alignItems: 'center', gap: 6, background: 'var(--erp-surface-alt)' }}>
          <Truck size={13} /> Procurement Details
        </div>
        <div className="erp-form-section">
          <div className="erp-form-3col">
            <FL label="Agent" value={form.agentName} onLookup={() => openLookup('Select Agent', '/api/AgentMaster/api/V1/GetAllAgents', [{ field: 'AgentId', header: 'ID' }, { field: 'AgentName', header: 'Name' }], r => { set('agentId', r.AgentId); set('agentName', r.AgentName); setLookup(null); })} testId="item-agent" />
            <FL label="Supplier" value={form.supplierName} required onLookup={() => openLookup('Select Supplier', '/api/supplier-master/list', [{ field: 'VendorId', header: 'ID' }, { field: 'VendorName', header: 'Name' }], r => { set('supplierId', r.VendorId); set('supplierName', r.VendorName); setLookup(null); })} testId="item-supplier" />
            <FS label="Payment Terms" value={form.paymentTerms} onChange={v => set('paymentTerms', v)} options={[{ value: 'cash', label: 'Cash' }, { value: 'credit', label: 'Credit' }, { value: 'advance', label: 'Advance' }]} testId="item-payterms" />
            <F label="Credit Period" value={form.creditPeriod} onChange={v => set('creditPeriod', v)} type="number" suffix="days" testId="item-credit" />
            <F label="Interest %" value={form.interest} onChange={v => set('interest', v)} type="number" suffix="%" testId="item-interest" />
            <FL label="Purch. Officer" value={form.purchOfficerName} onLookup={() => openLookup('Select Purchase Officer', '/api/PurchaseOfficerMaster/api/V1/GetAllPurchaseOfficers', [{ field: 'PurchaseOfficerId', header: 'ID' }, { field: 'PurchaseOfficerName', header: 'Name' }], r => { set('purchOfficerId', r.PurchaseOfficerId); set('purchOfficerName', r.PurchaseOfficerName); setLookup(null); })} testId="item-purchoff" />
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div className="erp-form-card">
        <div style={{ padding: '6px 12px', borderBottom: '1px solid var(--erp-border)', fontSize: 11, fontWeight: 700, color: '#b45309', display: 'flex', alignItems: 'center', gap: 6, background: 'var(--erp-surface-alt)' }}>
          <DollarSign size={13} /> Pricing
        </div>
        <div className="erp-form-section">
          <div className="erp-form-3col">
            <F label="Qty" value={form.priceQty} onChange={v => set('priceQty', v)} type="number" testId="item-pqty" />
            <F label="MRP" value={form.mrp} onChange={v => set('mrp', v)} type="number" required testId="item-mrp" />
            <F label="Free Qty" value={form.freeQty} onChange={v => set('freeQty', v)} type="number" testId="item-freeqty" />
            <F label="Disc %" value={form.discPer} onChange={v => set('discPer', v)} type="number" suffix="%" testId="item-disc" />
            <F label="Rate (Cost)" value={form.rate} onChange={v => set('rate', v)} type="number" required testId="item-rate" />
            <F label="Purch Disc %" value={form.purchDisc} onChange={v => set('purchDisc', v)} type="number" suffix="%" testId="item-pdisc" />
            <F label="Own MRP" value={form.ownMrp} onChange={v => set('ownMrp', v)} type="number" testId="item-ownmrp" />
            <F label="WSP" value={form.wsp} onChange={v => set('wsp', v)} type="number" testId="item-wsp" />
            <F label="List MRP" value={form.listMrp} onChange={v => set('listMrp', v)} type="number" testId="item-listmrp" />
          </div>
          {/* Markdown display */}
          <div style={{ display: 'flex', gap: 16, marginTop: 8, padding: '6px 0', borderTop: '1px solid var(--erp-border-light)' }}>
            <span style={{ fontSize: 10, fontWeight: 600 }}>WSP Markdown: <span style={{ fontFamily: 'var(--erp-font-mono)', color: '#e74c3c' }}>{form.wspMarkdown}%</span></span>
            <span style={{ fontSize: 10, fontWeight: 600 }}>MRP Markdown: <span style={{ fontFamily: 'var(--erp-font-mono)', color: '#e74c3c' }}>{form.mrpMarkdown}%</span></span>
          </div>
          <DynamicFieldRenderer screenName="ItemMaster_Pricing" recordId={form.transNo || form.skuPlu} token={token} values={dynPricing} onChange={(k, v) => setDynPricing(p => ({ ...p, [k]: v }))} readOnly={false} userRole={userRole} />
        </div>
      </div>

      {/* Classification + Dynamic Attributes */}
      <div className="erp-form-card">
        <div style={{ padding: '6px 12px', borderBottom: '1px solid var(--erp-border)', fontSize: 11, fontWeight: 700, color: '#1e40af', display: 'flex', alignItems: 'center', gap: 6, background: 'var(--erp-surface-alt)' }}>
          <Grid3X3 size={13} /> Classification
        </div>
        <div className="erp-form-section">
          <div className="erp-form-2col">
            <FL label="Division" value={form.divisionName} onLookup={() => openLookup('Select Division', '/api/Division/api/V1/GetAllDivisions', [{ field: 'DivisionId', header: 'ID' }, { field: 'DivisionName', header: 'Name' }], r => { set('divisionId', r.DivisionId); set('divisionName', r.DivisionName); setLookup(null); })} testId="item-div" />
            <FL label="Department" value={form.deptName} onLookup={() => openLookup('Select Department', '/api/Department/api/V1/GetAllDepartments', [{ field: 'DepartmentId', header: 'ID' }, { field: 'DepartmentName', header: 'Name' }], r => { set('deptId', r.DepartmentId); set('deptName', r.DepartmentName); setLookup(null); })} testId="item-dept" />
            <FL label="Category" value={form.categoryName} required onLookup={() => openLookup('Select Category', '/api/CategoryMaster/api/V1/GetAllCategories', [{ field: 'CategoryId', header: 'ID' }, { field: 'CategoryName', header: 'Name' }], r => { set('categoryId', r.CategoryId); set('categoryName', r.CategoryName); setLookup(null); })} testId="item-cat" />
            <FL label="Brand" value={form.brandName} onLookup={() => openLookup('Select Brand', '/api/BrandMaster/api/V1/GetAllBrands', [{ field: 'BrandId', header: 'ID' }, { field: 'BrandName', header: 'Name' }], r => { set('brandId', r.BrandId); set('brandName', r.BrandName); setLookup(null); })} testId="item-brand" />
          </div>
          {/* Dynamic classification attributes — admin can add new fields on demand */}
          <DynamicFieldRenderer screenName="ItemMaster_Classification" recordId={form.transNo || form.skuPlu} token={token} values={dynClassification} onChange={(k, v) => setDynClassification(p => ({ ...p, [k]: v }))} readOnly={false} userRole={userRole} />
        </div>
      </div>

      {/* Article Details — with dynamic fields */}
      <div className="erp-form-card">
        <div style={{ padding: '6px 12px', borderBottom: '1px solid var(--erp-border)', fontSize: 11, fontWeight: 700, color: '#059669', display: 'flex', alignItems: 'center', gap: 6, background: 'var(--erp-surface-alt)' }}>
          <FileText size={13} /> Article / Item Details
        </div>
        <div className="erp-form-section">
          <div className="erp-form-3col">
            <F label="Article Code" value={form.articleCode || 'Auto'} disabled testId="item-artcode" />
            <F label="Article Name" value={form.articleName} onChange={v => set('articleName', v)} testId="item-artname2" />
            <F label="Item Name" value={form.itemName} onChange={v => set('itemName', v)} required testId="item-name2" />
            <F label="Barcode" value={form.barcode || 'System Generated'} disabled testId="item-barcode" />
            <F label="OEM Barcode" value={form.oemBarcode} onChange={v => set('oemBarcode', v)} testId="item-oembar" />
            <F label="HSN Code" value={form.hsnCode} onChange={v => set('hsnCode', v)} testId="item-hsn2" />
          </div>
          {/* Dynamic article attributes — admin can add Size, Color, Style, Fit, etc. */}
          <DynamicFieldRenderer screenName="ItemMaster_Article" recordId={form.transNo || form.skuPlu} token={token} values={dynArticle} onChange={(k, v) => setDynArticle(p => ({ ...p, [k]: v }))} readOnly={false} userRole={userRole} />
        </div>
      </div>

      {/* Tax */}
      <div className="erp-form-card">
        <div style={{ padding: '6px 12px', borderBottom: '1px solid var(--erp-border)', fontSize: 11, fontWeight: 700, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 6, background: 'var(--erp-surface-alt)' }}>
          <Percent size={13} /> Tax Configuration
        </div>
        <div className="erp-form-section">
          <div className="erp-form-2col">
            <FL label="Input Tax (Purchase)" value={form.inputTaxName} onLookup={() => openLookup('Select Input Tax', '/api/TaxGroup/api/V1/GetAllTaxGroups', [{ field: 'TaxGroupId', header: 'ID' }, { field: 'TaxGroupName', header: 'Name' }, { field: 'TaxPercentage', header: '%' }], r => { set('inputTaxId', r.TaxGroupId); set('inputTaxName', r.TaxGroupName); setLookup(null); })} testId="item-itax" />
            <FL label="Output Tax (Sales)" value={form.outputTaxName} onLookup={() => openLookup('Select Output Tax', '/api/TaxGroup/api/V1/GetAllTaxGroups', [{ field: 'TaxGroupId', header: 'ID' }, { field: 'TaxGroupName', header: 'Name' }, { field: 'TaxPercentage', header: '%' }], r => { set('outputTaxId', r.TaxGroupId); set('outputTaxName', r.TaxGroupName); setLookup(null); })} testId="item-otax" />
            <F label="Cess" value={form.cessValue} onChange={v => set('cessValue', v)} type="number" suffix="%" testId="item-cess" />
            <FL label="Base Unit" value={form.baseUnitName} onLookup={() => openLookup('Select UOM', '/api/UOMMaster/api/v1/GetDetails', [{ field: 'UOMId', header: 'ID' }, { field: 'UOMName', header: 'Name' }], r => { set('baseUnitId', r.UOMId); set('baseUnitName', r.UOMName); setLookup(null); })} testId="item-uom" />
          </div>
        </div>
      </div>
    </div>
  );

  /* ═══════════════════════════════════════════
     TAB: MULTIPLE UNITS — dynamic rows + Add Row
  ═══════════════════════════════════════════ */
  // Ensure at least 6 initial unit rows
  useEffect(() => { if (unitRows.length === 0) setUnitRows(Array.from({ length: 6 }, (_, i) => ({ unitName: '', convQty: '', skuCode: '', eanCode: '', _rowId: `u${i}` }))); }, []);

  const addUnitRow = () => setUnitRows(prev => [...prev, { unitName: '', convQty: '', skuCode: '', eanCode: '', _rowId: `u${Date.now()}` }]);
  const deleteUnitRow = (idx) => { if (unitRows.length > 1) setUnitRows(prev => prev.filter((_, i) => i !== idx)); };
  const updateUnit = (idx, field, val) => { const next = [...unitRows]; next[idx] = { ...next[idx], [field]: val }; setUnitRows(next); };

  const renderUnitsTab = () => (
    <div className="erp-form-card">
      <div style={{ padding: '6px 12px', borderBottom: '1px solid var(--erp-border)', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, background: 'var(--erp-surface-alt)' }}>
        <Package size={13} /> Additional Units
      </div>
      <div className="erp-form-section">
        <div style={{ display: 'grid', gridTemplateColumns: '90px 160px 20px 90px 100px 130px 30px', gap: 6, alignItems: 'center', marginBottom: 6 }}>
          <span></span>
          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--erp-text-secondary)' }}>Unit Name</span>
          <span></span>
          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--erp-text-secondary)' }}>No Of Qty</span>
          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--erp-text-secondary)' }}>SKU Codes</span>
          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--erp-text-secondary)' }}>EAN/OEM Codes</span>
          <span></span>
        </div>
        {unitRows.map((row, i) => (
          <div key={row._rowId || i} style={{ display: 'grid', gridTemplateColumns: '90px 160px 20px 90px 100px 130px 30px', gap: 6, alignItems: 'center', marginBottom: 4 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--erp-text-secondary)' }}>Addl Unit{i + 1}</span>
            <div style={{ display: 'flex', gap: 2 }}>
              <input value={row.unitName || ''} readOnly className="erp-input" style={{ fontSize: 11, flex: 1 }} />
              <button className="erp-btn-icon" style={{ border: '1px solid var(--erp-border)' }} onClick={() => openLookup('Select UOM', '/api/UOMMaster/api/v1/GetDetails', [{ field: 'UOMId', header: 'ID' }, { field: 'UOMName', header: 'Name' }], r => { updateUnit(i, 'unitName', r.UOMName); setLookup(null); })}><Search size={11} /></button>
            </div>
            <span style={{ fontSize: 12, fontWeight: 700, textAlign: 'center' }}>=</span>
            <input value={row.convQty || ''} onChange={e => updateUnit(i, 'convQty', e.target.value)} className="erp-input" style={{ fontSize: 11, textAlign: 'right', fontFamily: 'var(--erp-font-mono)' }} type="number" placeholder="0" />
            <input value={row.skuCode || ''} onChange={e => updateUnit(i, 'skuCode', e.target.value)} className="erp-input" style={{ fontSize: 11 }} placeholder="Nos" />
            <input value={row.eanCode || ''} onChange={e => updateUnit(i, 'eanCode', e.target.value)} className="erp-input" style={{ fontSize: 11 }} />
            <button className="erp-btn-icon delete" onClick={() => deleteUnitRow(i)} title="Remove" style={{ padding: 2 }}><Trash2 size={11} /></button>
          </div>
        ))}
        <button onClick={addUnitRow} className="erp-btn erp-btn-primary" style={{ marginTop: 6, padding: '4px 12px', fontSize: 10 }} data-testid="item-add-unit-row"><Plus size={12} /> Add Row</button>
        <div style={{ borderTop: '1px solid var(--erp-border)', paddingTop: 10, marginTop: 10 }}>
          <div style={{ display: 'flex', gap: 24 }}>
            <FL label="Default Purchase Unit" value={form.defPurchUnit || ''} onLookup={() => openLookup('Select UOM', '/api/UOMMaster/api/v1/GetDetails', [{ field: 'UOMId', header: 'ID' }, { field: 'UOMName', header: 'Name' }], r => { set('defPurchUnit', r.UOMName); setLookup(null); })} testId="item-defpurch" />
            <FL label="Default Sales Unit" value={form.defSalesUnit || ''} onLookup={() => openLookup('Select UOM', '/api/UOMMaster/api/v1/GetDetails', [{ field: 'UOMId', header: 'ID' }, { field: 'UOMName', header: 'Name' }], r => { set('defSalesUnit', r.UOMName); setLookup(null); })} testId="item-defsales" />
          </div>
        </div>
      </div>
    </div>
  );

  /* ═══════════════════════════════════════════
     TAB: MULTIPLE RATES — dynamic rows + Add Row
  ═══════════════════════════════════════════ */
  useEffect(() => { if (rateRows.length === 0) setRateRows(Array.from({ length: 6 }, (_, i) => ({ unit: '', nos: i === 0 ? '0' : '', rate: '', wsp: '', mrp: '', discEnabled: true, discPer: '', splMrp: '0', _rowId: `r${i}` }))); }, []);

  const addRateRow = () => setRateRows(prev => [...prev, { unit: '', nos: '', rate: '', wsp: '', mrp: '', discEnabled: true, discPer: '', splMrp: '0', _rowId: `r${Date.now()}` }]);
  const deleteRateRow = (idx) => { if (rateRows.length > 1) setRateRows(prev => prev.filter((_, i) => i !== idx)); };
  const updateRate = (idx, field, val) => { const next = [...rateRows]; next[idx] = { ...next[idx], [field]: val }; setRateRows(next); };

  const renderRatesTab = () => (
    <div className="erp-form-card">
      <div style={{ padding: '6px 12px', borderBottom: '1px solid var(--erp-border)', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, background: 'var(--erp-surface-alt)' }}>
        <DollarSign size={13} /> Price List
      </div>
      <div className="erp-form-section">
        <div style={{ display: 'grid', gridTemplateColumns: '80px 60px 70px 90px 90px 90px 90px 30px 60px 80px 30px', gap: 4, marginBottom: 6, fontSize: 10, fontWeight: 700, color: 'var(--erp-text-secondary)' }}>
          <span>Units</span><span>No's</span><span></span><span>Rate</span><span>WSP</span><span>MRP</span><span>Discount</span><span></span><span></span><span>Spl. MRP</span><span></span>
        </div>
        {rateRows.map((row, i) => (
          <div key={row._rowId || i} style={{ display: 'grid', gridTemplateColumns: '80px 60px 70px 90px 90px 90px 90px 30px 60px 80px 30px', gap: 4, alignItems: 'center', marginBottom: 3 }}>
            <input value={row.unit || ''} onChange={e => updateRate(i, 'unit', e.target.value)} className="erp-input" style={{ fontSize: 11 }} />
            <input value={row.nos ?? ''} onChange={e => updateRate(i, 'nos', e.target.value)} className="erp-input" style={{ fontSize: 11, textAlign: 'right', fontFamily: 'var(--erp-font-mono)' }} type="number" />
            <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--erp-text-muted)', textAlign: 'center' }}>Price {i + 1}</span>
            <input value={row.rate || ''} onChange={e => updateRate(i, 'rate', e.target.value)} className="erp-input" style={{ fontSize: 11, textAlign: 'right', fontFamily: 'var(--erp-font-mono)' }} type="number" placeholder="NaN" />
            <input value={row.wsp || ''} onChange={e => updateRate(i, 'wsp', e.target.value)} className="erp-input" style={{ fontSize: 11, textAlign: 'right', fontFamily: 'var(--erp-font-mono)' }} type="number" placeholder="NaN" />
            <input value={row.mrp || ''} onChange={e => updateRate(i, 'mrp', e.target.value)} className="erp-input" style={{ fontSize: 11, textAlign: 'right', fontFamily: 'var(--erp-font-mono)' }} type="number" placeholder="NaN" />
            <span style={{ fontSize: 10, display: 'flex', alignItems: 'center', gap: 2 }}><input type="checkbox" checked={row.discEnabled !== false} onChange={e => updateRate(i, 'discEnabled', e.target.checked)} style={{ width: 13, height: 13 }} /> Disc. Per</span>
            <span></span>
            <input value={row.discPer || ''} onChange={e => updateRate(i, 'discPer', e.target.value)} className="erp-input" style={{ fontSize: 11, textAlign: 'right' }} type="number" />
            <input value={row.splMrp ?? '0'} onChange={e => updateRate(i, 'splMrp', e.target.value)} className="erp-input" style={{ fontSize: 11, textAlign: 'right', fontFamily: 'var(--erp-font-mono)' }} type="number" />
            <button className="erp-btn-icon delete" onClick={() => deleteRateRow(i)} title="Remove" style={{ padding: 2 }}><Trash2 size={11} /></button>
          </div>
        ))}
        <button onClick={addRateRow} className="erp-btn erp-btn-primary" style={{ marginTop: 6, padding: '4px 12px', fontSize: 10 }} data-testid="item-add-rate-row"><Plus size={12} /> Add Row</button>
      </div>
    </div>
  );

  /* ═══════════════════════════════════════════
     TAB: OTHER SUPPLIERS — entry form + grid + Update button
  ═══════════════════════════════════════════ */
  const [suppEntry, setSuppEntry] = useState({ suppName: '', leadTime: '', mrp: '', markup: '', markdown: '', partNo: '', partNo2: '', landingCost: '', basicCost: '' });
  const addSupplierRow = () => {
    if (!suppEntry.suppName) return;
    setSupplierRows(prev => [...prev, { ...suppEntry, _rowId: Date.now(), suppId: '' }]);
    setSuppEntry({ suppName: '', leadTime: '', mrp: '', markup: '', markdown: '', partNo: '', partNo2: '', landingCost: '', basicCost: '' });
  };
  const renderSuppliersTab = () => (
    <div className="erp-form-card">
      <div style={{ padding: '6px 12px', borderBottom: '1px solid var(--erp-border)', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, background: 'var(--erp-surface-alt)' }}>
        <Users size={13} /> Other Suppliers
      </div>
      <div className="erp-form-section">
        {/* Entry form row */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          <div style={{ display: 'flex', gap: 2, flex: '1 1 180px' }}>
            <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--erp-text-secondary)', alignSelf: 'center', minWidth: 50 }}>Supplier</label>
            <input value={suppEntry.suppName} readOnly className="erp-input" style={{ fontSize: 11, flex: 1 }} />
            <button className="erp-btn-icon" style={{ border: '1px solid var(--erp-border)' }} onClick={() => openLookup('Select Supplier', '/api/supplier-master/list', [{ field: 'VendorId', header: 'ID' }, { field: 'VendorName', header: 'Name' }], r => { setSuppEntry(p => ({ ...p, suppName: r.VendorName, suppId: r.VendorId })); setLookup(null); })}><Search size={11} /></button>
          </div>
          <F label="Lead Time" value={suppEntry.leadTime} onChange={v => setSuppEntry(p => ({ ...p, leadTime: v }))} type="number" style={{ flex: '0 0 80px' }} />
          <F label="MRP" value={suppEntry.mrp} onChange={v => setSuppEntry(p => ({ ...p, mrp: v }))} type="number" style={{ flex: '0 0 80px' }} />
          <F label="Markup" value={suppEntry.markup} onChange={v => setSuppEntry(p => ({ ...p, markup: v }))} type="number" style={{ flex: '0 0 80px' }} />
          <F label="Markdown" value={suppEntry.markdown} onChange={v => setSuppEntry(p => ({ ...p, markdown: v }))} type="number" style={{ flex: '0 0 80px' }} />
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          <F label="PartNo" value={suppEntry.partNo} onChange={v => setSuppEntry(p => ({ ...p, partNo: v }))} style={{ flex: '0 0 100px' }} />
          <F label="" value={suppEntry.partNo2} onChange={v => setSuppEntry(p => ({ ...p, partNo2: v }))} style={{ flex: '0 0 120px' }} />
          <F label="Landing Cost" value={suppEntry.landingCost} onChange={v => setSuppEntry(p => ({ ...p, landingCost: v }))} type="number" style={{ flex: '0 0 90px' }} />
          <F label="Basic Cost" value={suppEntry.basicCost} onChange={v => setSuppEntry(p => ({ ...p, basicCost: v }))} type="number" style={{ flex: '0 0 90px' }} />
          <button className="erp-btn erp-btn-primary" onClick={addSupplierRow} style={{ alignSelf: 'flex-end', height: 28 }} data-testid="item-add-supp"><Plus size={12} /> Add</button>
        </div>
        {/* Supplier grid */}
        <div className="erp-grid-wrapper">
          <table className="erp-grid" style={{ minWidth: 'unset' }}>
            <thead><tr><th>SuppId</th><th>SupName</th><th>Lead Time</th><th>MRP</th><th>Mark Up</th><th>Mark Down</th><th>Del</th></tr></thead>
            <tbody>
              {supplierRows.length === 0 ? <tr><td colSpan={7} style={{ textAlign: 'center', padding: 16, color: 'var(--erp-text-muted)', fontSize: 11 }}>No suppliers added</td></tr> :
                supplierRows.map((r, i) => (
                  <tr key={r._rowId || i}>
                    <td className="cell-mono">{r.suppId || '-'}</td>
                    <td className="cell-bold">{r.suppName}</td>
                    <td>{r.leadTime}</td><td style={{ fontFamily: 'var(--erp-font-mono)' }}>{r.mrp}</td>
                    <td>{r.markup}</td><td>{r.markdown}</td>
                    <td><button className="erp-btn-icon delete" onClick={() => setSupplierRows(prev => prev.filter((_, j) => j !== i))}><Trash2 size={11} /></button></td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
          <span style={{ fontSize: 10, fontWeight: 600 }}>Total No Of Supplier : {supplierRows.length}</span>
          <button className="erp-btn erp-btn-ghost" data-testid="item-update-supps">Update Suppliers</button>
        </div>
      </div>
    </div>
  );

  /* ═══════════════════════════════════════════
     TAB: CONTROL OPTIONS — EAN/OEM Control Setup matching reference
  ═══════════════════════════════════════════ */
  const renderControlTab = () => (
    <div className="erp-form-card">
      <div style={{ padding: '6px 12px', borderBottom: '1px solid var(--erp-border)', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, background: 'var(--erp-surface-alt)' }}>
        <ToggleLeft size={13} /> EAN/OEM CONTROL SETUP
      </div>
      <div className="erp-form-section" style={{ maxWidth: 500, margin: '0 auto', padding: '20px var(--erp-sp-4)' }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginBottom: 20 }}>
          <div style={{ border: '1px solid var(--erp-border)', borderRadius: 6, padding: '10px 24px', display: 'flex', gap: 20 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}><input type="radio" name="invType" value="inventory" checked={form.invType === 'inventory'} onChange={() => set('invType', 'inventory')} /> Inventory</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}><input type="radio" name="invType" value="non-inventory" checked={form.invType === 'non-inventory'} onChange={() => set('invType', 'non-inventory')} /> Non Inventory</label>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginBottom: 24 }}>
          <div style={{ border: '1px solid var(--erp-border)', borderRadius: 6, padding: '10px 24px', display: 'flex', gap: 20 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}><input type="radio" name="itemStatus" value="active" checked={form.itemStatus === 'active'} onChange={() => set('itemStatus', 'active')} /> Active</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer' }}><input type="radio" name="itemStatus" value="inactive" checked={form.itemStatus === 'inactive'} onChange={() => set('itemStatus', 'inactive')} /> Inactive</label>
          </div>
        </div>
        {/* Date ranges */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <label style={{ fontSize: 11, fontWeight: 600, width: 140, color: 'var(--erp-text-secondary)' }}>Accept Purchase From</label>
          <input type="date" value={form.accPurchFrom || '2025-04-01'} onChange={e => set('accPurchFrom', e.target.value)} className="erp-input" style={{ fontSize: 11, width: 130 }} />
          <span style={{ fontSize: 11, fontWeight: 600 }}>To</span>
          <input type="date" value={form.accPurchTo || '2026-03-31'} onChange={e => set('accPurchTo', e.target.value)} className="erp-input" style={{ fontSize: 11, width: 130 }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <label style={{ fontSize: 11, fontWeight: 600, width: 140, color: 'var(--erp-text-secondary)' }}>Accept Sales From</label>
          <input type="date" value={form.accSalesFrom || '2025-04-01'} onChange={e => set('accSalesFrom', e.target.value)} className="erp-input" style={{ fontSize: 11, width: 130 }} />
          <span style={{ fontSize: 11, fontWeight: 600 }}>To</span>
          <input type="date" value={form.accSalesTo || '2026-03-31'} onChange={e => set('accSalesTo', e.target.value)} className="erp-input" style={{ fontSize: 11, width: 130 }} />
        </div>
        <DynamicFieldRenderer screenName="ItemMaster_Control" recordId={form.transNo || form.skuPlu} token={token} values={dynControl} onChange={(k, v) => setDynControl(p => ({ ...p, [k]: v }))} readOnly={false} userRole={userRole} />
      </div>
    </div>
  );

  /* ═══════════════════════════════════════════
     TAB: BRANDING
  ═══════════════════════════════════════════ */
  const renderBrandingTab = () => (
    <div className="erp-form-card">
      <div style={{ padding: '6px 12px', borderBottom: '1px solid var(--erp-border)', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, background: 'var(--erp-surface-alt)' }}>
        <Palette size={13} /> Branding & Revenue
      </div>
      <div className="erp-form-section">
        <div className="erp-form-2col">
          <F label="Display Item Revenue" value={form.displayItemRevenue} onChange={v => set('displayItemRevenue', v)} testId="item-revenue" />
          <F label="Branding Image" value={form.brandingImage} onChange={v => set('brandingImage', v)} testId="item-brandimg" />
          <div className="erp-form-row">
            <label className="erp-form-label">Promoter Support</label>
            <div className="erp-form-control" style={{ display: 'flex', gap: 16 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}><input type="radio" name="promoter" value="yes" checked={form.promoterSupport === 'yes'} onChange={() => set('promoterSupport', 'yes')} /> Yes</label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12 }}><input type="radio" name="promoter" value="no" checked={form.promoterSupport === 'no'} onChange={() => set('promoterSupport', 'no')} /> No</label>
            </div>
          </div>
          <F label="Revenue 1" value={form.revenue1} onChange={v => set('revenue1', v)} testId="item-rev1" />
          <F label="Revenue 2" value={form.revenue2} onChange={v => set('revenue2', v)} testId="item-rev2" />
          <F label="Revenue 3" value={form.revenue3} onChange={v => set('revenue3', v)} testId="item-rev3" />
        </div>
      </div>
    </div>
  );

  /* ═══════════════════════════════════════════
     TAB: IMPORT
  ═══════════════════════════════════════════ */
  const [importFile, setImportFile] = useState(null);
  const [importFileName, setImportFileName] = useState('');
  const fileInputRef = useCallback(node => { if (node) node.value = ''; }, []);
  const renderImportTab = () => (
    <div className="erp-form-card">
      <div style={{ padding: '6px 12px', borderBottom: '1px solid var(--erp-border)', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, background: 'var(--erp-surface-alt)' }}>
        <Upload size={13} /> Import Products
      </div>
      <div className="erp-form-section">
        {/* File selector row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--erp-text-secondary)', whiteSpace: 'nowrap' }}>Select ExcelFile:</label>
          <input value={importFileName} readOnly className="erp-input" style={{ flex: 1, fontSize: 11 }} placeholder="No file selected" />
          <input type="file" accept=".xlsx,.xls,.csv,.pdf" style={{ display: 'none' }} id="item-import-file" onChange={e => { const f = e.target.files?.[0]; if (f) { setImportFile(f); setImportFileName(f.name); } }} />
          <button className="erp-btn erp-btn-ghost" onClick={() => document.getElementById('item-import-file')?.click()} style={{ fontWeight: 700 }} data-testid="item-import-browse">Browse</button>
          <button className="erp-btn erp-btn-ghost" onClick={() => { setImportFile(null); setImportFileName(''); }} data-testid="item-import-refresh"><RefreshCw size={12} /> Refresh</button>
        </div>
        {/* Preview area */}
        <div style={{ border: '1px solid var(--erp-border)', borderRadius: 6, minHeight: 300, background: 'var(--erp-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {importFile ? (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <FileText size={32} style={{ color: 'var(--erp-primary)', marginBottom: 8 }} />
              <div style={{ fontSize: 12, fontWeight: 600 }}>{importFileName}</div>
              <div style={{ fontSize: 10, color: 'var(--erp-text-muted)', marginTop: 4 }}>{(importFile.size / 1024).toFixed(1)} KB</div>
              <div style={{ marginTop: 12, display: 'flex', gap: 8, justifyContent: 'center' }}>
                <button className="erp-btn erp-btn-primary" data-testid="item-import-process">Process Import</button>
              </div>
            </div>
          ) : (
            <span style={{ fontSize: 11, color: 'var(--erp-text-muted)' }}>-</span>
          )}
        </div>
      </div>
    </div>
  );

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'sku': return renderSKUTab();
      case 'units': return renderUnitsTab();
      case 'rates': return renderRatesTab();
      case 'suppliers': return renderSuppliersTab();
      case 'control': return renderControlTab();
      case 'branding': return renderBrandingTab();
      case 'import': return renderImportTab();
      default: return null;
    }
  };

  return (
    <div className="erp-module" data-testid="item-creation-screen">
      {/* ─── Screen Header ─── */}
      <div className="erp-screen-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {onClose && <button onClick={onClose} className="erp-btn erp-btn-ghost" style={{ padding: '3px 8px', fontSize: 11, gap: 4 }} data-testid="screen-back-btn"><ChevronLeft size={14} /> BACK</button>}
          <span className="erp-screen-title">Product Master</span>
          <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 10, background: 'var(--erp-info-light)', color: 'var(--erp-info)' }}>{itemCount} Items</span>
        </div>
        <div className="erp-screen-actions">
          <button className="erp-btn erp-btn-ghost" onClick={handleSave} disabled={saving} data-testid="item-save-btn">
            <Save size={13} /> {saving ? 'Saving...' : 'Save'}
          </button>
          <button className="erp-btn erp-btn-ghost" onClick={handleClear}><RotateCcw size={13} /> Clear</button>
        </div>
      </div>

      {error && <div className="erp-alert-error"><AlertCircle size={13} /> {error}</div>}

      {/* ─── Main Content ─── */}
      <form style={{ flex: 1, overflow: 'auto', padding: 'var(--erp-sp-3)' }} onSubmit={e => e.preventDefault()}>
        {/* Header Fields Card */}
        <div className="erp-form-card" style={{ marginBottom: 10 }}>
          <div style={{ padding: '6px 12px', borderBottom: '1px solid var(--erp-border)', fontSize: 11, fontWeight: 700, color: 'var(--erp-text)', display: 'flex', alignItems: 'center', gap: 6, background: 'var(--erp-surface-alt)' }}>
            <FileText size={13} /> Product Identification
          </div>
          <div className="erp-form-section">
            <div className="erp-form-3col">
              <F label="Trans No" value={form.transNo || 'Auto Generated'} disabled testId="item-transno" />
              <F label="SKU / PLU" value={form.skuPlu} onChange={v => set('skuPlu', v)} required testId="item-sku" placeholder="Auto / Manual" />
              <F label="EAN / OEM Barcode" value={form.eanCode} onChange={v => set('eanCode', v)} testId="item-ean" />
              <FL label="Manufacturer" value={form.manufacturerName} onLookup={() => openLookup('Select Manufacturer', '/api/supplier-master/list', [{ field: 'VendorId', header: 'ID' }, { field: 'VendorName', header: 'Name' }], r => { set('manufacturerId', r.VendorId); set('manufacturerName', r.VendorName); setLookup(null); })} testId="item-mfr" />
              <F label="Article Name" value={form.articleName} onChange={v => set('articleName', v)} testId="item-artname" />
              <F label="Item Name" value={form.itemName} onChange={v => set('itemName', v)} required testId="item-name" />
              <F label="HSN Code" value={form.hsnCode} onChange={v => set('hsnCode', v)} testId="item-hsn" />
            </div>
          </div>
        </div>

        {/* Inventory Metrics Strip */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <MetricCard label="Min Qty" value={form.minQty} icon={Box} color="#7c3aed" />
          <MetricCard label="ROL" value={form.rol} icon={AlertCircle} color="#dc2626" />
          <MetricCard label="Max Qty" value={form.maxQty} icon={Warehouse} color="#0369a1" />
          <MetricCard label="Lead Time" value={`${form.leadTime}d`} icon={Clock} color="#b45309" />
          <MetricCard label="Exp Period" value={`${form.expPeriod}d`} icon={Calendar} color="#15803d" />
        </div>

        {/* Editable Metrics */}
        <div className="erp-form-card" style={{ marginBottom: 10 }}>
          <div className="erp-form-section" style={{ padding: '6px var(--erp-sp-3)' }}>
            <div style={{ display: 'flex', gap: 12 }}>
              <F label="Min Qty" value={form.minQty} onChange={v => set('minQty', v)} type="number" style={{ flex: 1 }} />
              <F label="ROL" value={form.rol} onChange={v => set('rol', v)} type="number" style={{ flex: 1 }} />
              <F label="Max Qty" value={form.maxQty} onChange={v => set('maxQty', v)} type="number" style={{ flex: 1 }} />
              <F label="Lead Time" value={form.leadTime} onChange={v => set('leadTime', v)} type="number" suffix="days" style={{ flex: 1 }} />
              <F label="Exp Period" value={form.expPeriod} onChange={v => set('expPeriod', v)} type="number" suffix="days" style={{ flex: 1 }} />
            </div>
          </div>
        </div>

        {/* ─── Tabs ─── */}
        <div className="erp-tabs" style={{ marginBottom: 0 }}>
          {TABS.map(tab => (
            <button key={tab.id} className={`erp-tab${activeTab === tab.id ? ' active' : ''}`} onClick={() => setActiveTab(tab.id)} data-testid={`item-tab-${tab.id}`}>
              <tab.icon size={12} style={{ marginRight: 4 }} />{tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ paddingTop: 10 }}>
          {renderActiveTab()}
        </div>
      </form>

      {/* Lookup Modal */}
      {lookup && <LookupModal title={lookup.title} apiUrl={lookup.apiUrl} columns={lookup.columns} onSelect={lookup.onSelect} onClose={() => setLookup(null)} />}
    </div>
  );
};

export default ItemCreationScreen;
